import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  MouseEvent as ReactMouseEvent,
  MouseEventHandler,
  ReactElement,
} from 'react';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { ButtonLinkProps, IButtonProps, NativeButtonProps } from './button.interface';
import styles from './button.module.scss';

const DISABLED_LINK_TAB_INDEX = -1;

interface ButtonComponent {
  (props: NativeButtonProps): ReactElement;
  (props: ButtonLinkProps): ReactElement;
}

const Button: ButtonComponent = (props: IButtonProps): ReactElement => {
  const {
    onClick,
    link,
    size = 'large',
    mode = 'primary',
    color = 'accent',
    shape = 'default',
    type = 'button',
    modBlock,
    icon,
    children,
    disabled,
    className,
    ...restProps
  } = props;
  const isIconOnly = Boolean(icon) && !children;

  const buttonClassNames = classNames(styles.button, className, {
    [styles[`${size}`]]: size,
    [styles[`${mode}`]]: mode,
    [styles[`${color}`]]: color,
    [styles[`${shape}`]]: shape,
    [styles.block]: modBlock,
    [styles.disabled]: disabled,
    [styles['icon-only']]: isIconOnly,
  });

  if (link !== undefined) {
    const linkProps = restProps as AnchorHTMLAttributes<HTMLAnchorElement>;
    const linkOnClick = onClick as MouseEventHandler<HTMLAnchorElement> | undefined;
    const handleLinkClick = (event: ReactMouseEvent<HTMLAnchorElement>): void => {
      if (disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      linkOnClick?.(event);
    };

    return (
      <Link
        {...linkProps}
        to={link}
        className={buttonClassNames}
        onClick={handleLinkClick}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? DISABLED_LINK_TAB_INDEX : linkProps.tabIndex}
      >
        {icon && icon}
        {children}
      </Link>
    );
  }

  const buttonProps = restProps as ButtonHTMLAttributes<HTMLButtonElement>;

  return (
    <button
      {...buttonProps}
      onClick={onClick}
      className={buttonClassNames}
      type={type}
      disabled={disabled}
    >
      {icon && icon}
      {children}
    </button>
  );
};

export { Button };
